import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { CONFIG } from '../config';
import { MEDIA } from '../config/media';

const { FiSend, FiCheckCircle, FiAlertCircle, FiPhone, FiMail } = FiIcons;

// SECTION 01 – CUSTOMISABLE EMAIL BRANDING
const EMAIL_BRAND_NAME = "BPA Solutions";
const EMAIL_BRAND_PRIMARY_COLOR = "#1e3a8a";
const EMAIL_BRAND_ACCENT_COLOR = "#e5edff";
const EMAIL_LOGO_URL = MEDIA.LOGO_EMAIL.src; 

const EMAIL_HEADER_TITLE_A = "New Website Enquiry";
const EMAIL_HEADER_TITLE_B = "We Have Received Your Enquiry";

const EMAIL_FOOTER_TEXT = "This message was sent automatically. If you need help, reply to this email or contact our team.";

const ACK_SIGNOFF_LINES = [
  "Kind regards,",
  "BPA Solutions - Client Success"
];

// SENDER NAMES
const SENDER_NAME_A = "BPA Solutions - Services Website Enquiry";
const SENDER_NAME_B = "BPA Solutions - Client Succcess";

// Fixed Settings
const LEAD_NOTIFICATION_EMAIL = "anish.m.malhotra@gmail.com";
const EMAIL_DELAY_MS = 4000;

// HELPER FUNCTIONS
const escapeHtml = (val) => {
  if (val === undefined || val === null || val === '') return "-";
  return String(val)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
};

const generateTableRows = (payload) => {
  return Object.entries(payload)
    .map(([key, value], index) => {
      const label = key.charAt(0).toUpperCase() + key.slice(1);
      const val = value ? escapeHtml(value) : "-";
      const bg = index % 2 === 0 ? "#ffffff" : EMAIL_BRAND_ACCENT_COLOR;
      return `
        <tr style="background-color: ${bg};">
          <td style="padding: 12px; border: 1px solid #e2e8f0; color: #64748b; font-weight: 600; width: 40%; font-size: 13px;">${label}</td>
          <td style="padding: 12px; border: 1px solid #e2e8f0; color: #1e293b; font-size: 13px;">${val}</td>
        </tr>
      `;
    }).join('');
};

const buildBrandedEmailHtml = ({ title, introHtml, tableRows, signoffHtml, footerText }) => {
  const logoHtml = EMAIL_LOGO_URL ? `<img src="${EMAIL_LOGO_URL}" alt="${EMAIL_BRAND_NAME}" style="max-height: 40px; margin-bottom: 20px; display: block; margin-left: auto; margin-right: auto;">` : "";
  
  return `
    <div style="background-color: #f3f4f6; padding: 40px 20px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;">
      <div style="max-width: 600px; margin: 0 auto; background-color: #ffffff; border-radius: 12px; overflow: hidden; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);">
        <div style="background-color: ${EMAIL_BRAND_PRIMARY_COLOR}; padding: 30px; text-align: center;">
          ${logoHtml}
          <h1 style="color: #ffffff; margin: 0; font-size: 22px; font-weight: 700; letter-spacing: -0.02em;">${title}</h1>
        </div>
        <div style="padding: 40px; color: #334155;">
          <div style="font-size: 16px; line-height: 1.6; margin-bottom: 30px;">
            ${introHtml}
          </div>
          <table style="width: 100%; border-collapse: collapse; margin-bottom: 30px;">
            <thead>
              <tr>
                <th colspan="2" style="text-align: left; padding: 12px; background-color: ${EMAIL_BRAND_ACCENT_COLOR}; color: ${EMAIL_BRAND_PRIMARY_COLOR}; border-bottom: 2px solid ${EMAIL_BRAND_PRIMARY_COLOR}; border-radius: 4px 4px 0 0; font-size: 14px; text-transform: uppercase; letter-spacing: 0.05em;">Submission Details</th>
              </tr>
            </thead>
            <tbody>
              ${tableRows}
            </tbody>
          </table>
          ${signoffHtml ? `
            <div style="font-size: 15px; line-height: 1.6; color: #475569; border-top: 1px solid #e2e8f0; margin-top: 30px; padding-top: 20px;">
              ${signoffHtml}
            </div>
          ` : ''}
        </div>
        <div style="background-color: #f8fafc; padding: 20px; text-align: center; border-top: 1px solid #e2e8f0;">
          <p style="color: #94a3b8; font-size: 12px; margin: 0;">${footerText}</p>
        </div>
      </div>
    </div>
  `;
};

const QuoteForm = () => {
  const EMAILIT_API_KEY = import.meta.env.VITE_EMAILIT_API_KEY;
  const EMAILIT_FROM = import.meta.env.VITE_EMAILIT_FROM;

  const [step, setStep] = useState('form');
  const [sendingStep, setSendingStep] = useState('');
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', business: '',
    industry: '', location: '', website: '', revenue: '',
    goal: '', demo: '', notes: ''
  });
  const [errors, setErrors] = useState({});
  const [submissionError, setSubmissionError] = useState('');

  const validate = () => {
    const newErrors = {};
    if (!formData.name) newErrors.name = "Required";
    if (!formData.email || !/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = "Valid email required";
    if (!formData.phone) newErrors.phone = "Required";
    if (!formData.business) newErrors.business = "Required";
    if (!formData.industry) newErrors.industry = "Required";
    if (!formData.location) newErrors.location = "Required";
    if (!formData.revenue) newErrors.revenue = "Required";
    if (!formData.goal) newErrors.goal = "Required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const sendEmailIt = async (from, toEmail, subject, textBody, htmlBody) => {
    if (!EMAILIT_API_KEY || !EMAILIT_FROM) {
      throw new Error("Missing VITE_EMAILIT_API_KEY or VITE_EMAILIT_FROM in .env");
    }

    const res = await fetch('https://api.emailit.com/v1/emails', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${EMAILIT_API_KEY}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        from: from,
        to: toEmail,
        subject: subject,
        text: textBody,
        html: htmlBody
      })
    });

    const text = await res.text();
    let parsedJson = {};
    try {
      parsedJson = JSON.parse(text);
    } catch (e) {
      // Not JSON
    }

    if (!res.ok) {
      if (parsedJson.error) {
        throw new Error(parsedJson.error);
      } else {
        throw new Error(`Server returned ${res.status}. ${text.substring(0, 160)}`);
      }
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    
    setStep('loading');
    setSendingStep("Sending your enquiry, please wait");
    setSubmissionError('');

    const timestamp = new Date().toLocaleString('en-AU');
    
    // 1. Build textSummary (Plain Text Fallback)
    const textSummary = `
NEW WEBSITE ENQUIRY
-------------------------------
Timestamp: ${timestamp}
${Object.entries(formData).map(([k, v]) => `${k.charAt(0).toUpperCase() + k.slice(1)}: ${v || 'N/A'}`).join('\n')}
    `.trim();

    // 2. Build Branded HTML Content
    const rows = generateTableRows(formData);

    const brandedHtmlA = buildBrandedEmailHtml({
      title: EMAIL_HEADER_TITLE_A,
      introHtml: "A new website enquiry has been submitted. Details are below.",
      tableRows: rows,
      signoffHtml: "",
      footerText: EMAIL_FOOTER_TEXT
    });

    const brandedHtmlB = buildBrandedEmailHtml({
      title: EMAIL_HEADER_TITLE_B,
      introHtml: `Hi ${escapeHtml(formData.name)}, thanks for your enquiry. Our team has received your details and will be in touch soon.`,
      tableRows: rows,
      signoffHtml: ACK_SIGNOFF_LINES.map(line => escapeHtml(line)).join('<br/>'),
      footerText: EMAIL_FOOTER_TEXT
    });

    try {
      // Step 1: Send Email A (Notification)
      setSendingStep("Notifying our team");
      const fromA = `${SENDER_NAME_A} <${EMAILIT_FROM}>`;
      await sendEmailIt(fromA, LEAD_NOTIFICATION_EMAIL, `New lead received - ${formData.business}`, textSummary, brandedHtmlA);

      // Step 2: Delay
      setSendingStep("Preparing your confirmation email");
      await new Promise(resolve => setTimeout(resolve, EMAIL_DELAY_MS));

      // Step 3: Send Email B (Acknowledgement)
      setSendingStep("Sending confirmation to your email");
      const fromB = `${SENDER_NAME_B} <${EMAILIT_FROM}>`;
      await sendEmailIt(fromB, formData.email, "We received your enquiry", textSummary, brandedHtmlB);

      setStep('success');
    } catch (err) {
      const errorMsg = err.message === 'Failed to fetch' ? "Network error. Failed to fetch." : err.message;
      setSubmissionError(errorMsg);
      setStep('error');
    }
  };

  return (
    <div id="quote" className="max-w-4xl mx-auto">
      <motion.div 
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="glass-strong rounded-[3rem] overflow-hidden border border-white/10"
      >
        <div className="grid lg:grid-cols-5">
          <div className="lg:col-span-2 bg-blue-600 p-12 md:p-16 flex flex-col justify-between text-white relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-indigo-800" />
            <div className="relative z-10">
              <h2 className="text-3xl md:text-4xl font-bold font-display mb-6 leading-tight">Request a Quote</h2>
              <p className="text-blue-100 text-sm leading-relaxed mb-12">
                Tell us about your practice. We'll provide a fixed-scope proposal within 24 hours.
              </p>
              <div className="space-y-8">
                <div className="flex items-center gap-5">
                  <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center border border-white/20">
                    <SafeIcon icon={FiPhone} />
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-blue-200 font-black">Direct Line</p>
                    <p className="font-bold">{CONFIG.SUPPORT_PHONE}</p>
                  </div>
                </div>
                <div className="flex items-center gap-5">
                  <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center border border-white/20">
                    <SafeIcon icon={FiMail} />
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-blue-200 font-black">Email</p>
                    <p className="font-bold">{CONFIG.SUPPORT_EMAIL}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-3 p-12 md:p-16 bg-[#0a0c10]">
            {step === 'form' && (
              <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2 space-y-1">
                  <label className="premium-label">Full Name *</label>
                  <input className="premium-input" placeholder="Your name" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} />
                  {errors.name && <p className="text-red-500 text-[10px] font-bold">{errors.name}</p>}
                </div>
                <div className="space-y-1">
                  <label className="premium-label">Email *</label>
                  <input type="email" className="premium-input" placeholder="email@firm.au" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} />
                  {errors.email && <p className="text-red-500 text-[10px] font-bold">{errors.email}</p>}
                </div>
                <div className="space-y-1">
                  <label className="premium-label">Phone *</label>
                  <input className="premium-input" placeholder="0400 000 000" value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} />
                  {errors.phone && <p className="text-red-500 text-[10px] font-bold">{errors.phone}</p>}
                </div>
                <div className="md:col-span-2 space-y-1">
                  <label className="premium-label">Business Name *</label>
                  <input className="premium-input" placeholder="Practice Name" value={formData.business} onChange={e => setFormData({...formData, business: e.target.value})} />
                  {errors.business && <p className="text-red-500 text-[10px] font-bold">{errors.business}</p>}
                </div>
                <div className="space-y-1">
                  <label className="premium-label">Industry *</label>
                  <select className="premium-input appearance-none" value={formData.industry} onChange={e => setFormData({...formData, industry: e.target.value})}>
                    <option value="">Select Industry</option>
                    {CONFIG.INDUSTRIES.map(m => <option key={m} value={m} className="bg-[#080a0d]">{m}</option>)}
                  </select>
                  {errors.industry && <p className="text-red-500 text-[10px] font-bold">{errors.industry}</p>}
                </div>
                <div className="space-y-1">
                  <label className="premium-label">Location *</label>
                  <input className="premium-input" placeholder="e.g. Sydney, NSW" value={formData.location} onChange={e => setFormData({...formData, location: e.target.value})} />
                  {errors.location && <p className="text-red-500 text-[10px] font-bold">{errors.location}</p>}
                </div>
                <div className="space-y-1">
                  <label className="premium-label">Primary Goal *</label>
                  <select className="premium-input appearance-none" value={formData.goal} onChange={e => setFormData({...formData, goal: e.target.value})}>
                    <option value="">Select Goal</option>
                    {CONFIG.PRIMARY_GOALS.map(g => <option key={g} value={g} className="bg-[#080a0d]">{g}</option>)}
                  </select>
                  {errors.goal && <p className="text-red-500 text-[10px] font-bold">{errors.goal}</p>}
                </div>
                <div className="space-y-1">
                  <label className="premium-label">Revenue *</label>
                  <select className="premium-input appearance-none" value={formData.revenue} onChange={e => setFormData({...formData, revenue: e.target.value})}>
                    <option value="">Select Revenue</option>
                    {CONFIG.REVENUE_RANGES.map(r => <option key={r} value={r} className="bg-[#080a0d]">{r}</option>)}
                  </select>
                  {errors.revenue && <p className="text-red-500 text-[10px] font-bold">{errors.revenue}</p>}
                </div>
                <div className="md:col-span-2 pt-6">
                  <button type="submit" disabled={step === 'loading'} className="btn-premium-primary w-full group py-6 disabled:opacity-50 disabled:cursor-not-allowed">
                    Request Strategy Quote
                    <SafeIcon icon={FiSend} className="group-hover:translate-x-1 transition-transform" />
                  </button>
                </div>
              </form>
            )}

            {step === 'loading' && (
              <div className="py-24 text-center">
                <div className="w-16 h-16 border-4 border-blue-500/20 border-t-blue-500 rounded-full animate-spin mx-auto mb-10" />
                <h3 className="text-2xl font-bold text-white mb-4">Securing Transmission</h3>
                <p className="text-slate-400 text-sm animate-pulse">{sendingStep}</p>
              </div>
            )}

            {step === 'error' && (
              <div className="py-24 text-center">
                <SafeIcon icon={FiAlertCircle} className="text-5xl text-red-500 mx-auto mb-10" />
                <h3 className="text-2xl font-bold text-white mb-4">Submission Error</h3>
                <p className="text-slate-400 text-sm mb-10 px-4">{submissionError}</p>
                <button onClick={() => setStep('form')} className="btn-premium-secondary w-full">Try Again</button>
              </div>
            )}

            {step === 'success' && (
              <div className="py-24 text-center">
                <SafeIcon icon={FiCheckCircle} className="text-5xl text-green-500 mx-auto mb-10" />
                <h3 className="text-3xl font-bold text-white mb-6">Request Received</h3>
                <p className="text-slate-200 text-sm font-bold mb-4">Thanks, {formData.name}. We have received your enquiry and our team will be in touch soon.</p>
                <p className="text-slate-400 text-xs">A confirmation has been sent to {formData.email}.</p>
                <button onClick={() => setStep('form')} className="mt-12 btn-premium-secondary w-full">Send Another Enquiry</button>
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default QuoteForm;